import {
    getDocumentExtractedFields,
    type ICustomFieldPropertyResponse,
    type ICustomFieldResponse,
    type IDocumentExtractedFieldItem,
    type IDocumentExtractedFieldsResponse,
    type IDocumentFieldsByPolicyType,
    type IExtractedCOIResponse,
    type IExtractedFieldResponse,
    type IExtractedPropertyValue,
} from "@/api/documents";
import { Button } from "@/components/ui/button";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/auth/useAuth";
import { isEmptyPropertyValue } from "@/utils/fields";
import { useQuery } from "@tanstack/react-query";
import {
    AlertCircle,
    BadgeCheck,
    ChevronRight,
    FileSearch2,
    Inbox,
    Shield,
    Sparkles,
    TriangleAlert,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type Ref } from "react";
import { AddCustomFieldButton } from "./AddCustomFieldButton";
import { AddPropertyInlineForm } from "./AddPropertyInlineForm";
import { AdminExtractionMenu } from "./AdminExtractionMenu";
import { ExtractedCOISection } from "./ExtractedCOISection";
import { ExtractedFieldRow } from "./ExtractedFieldRow";
import { countHiddenFields } from "./extraction-utils";
import { FieldsLoadingState } from "./FieldsLoadingState";
import { FieldsToolbar } from "./FieldsToolbar";
import { RunExtractionButton } from "./RunExtractionButton";

const COI_LABEL = "Certificate of Insurance";
const POLICY_FALLBACK_LABEL = "Policy fields";

interface ExtractedFieldsSectionProps {
    documentId: number;
    /** Optional override. When omitted, the section runs its own fields query
     *  so MSW-driven tests can exercise the error stub without a panel. */
    data?: IDocumentExtractedFieldsResponse;
    coi?: IExtractedCOIResponse | null;
    isLoading?: boolean;
    /** Optional override for parent-driven error state. */
    isError?: boolean;
    /** Optional retry callback paired with `isError`. */
    onRetry?: () => void;
    /** Current visible PDF page (1-indexed), forwarded to add-field / add-property forms */
    currentPage?: number;
    /** Total pages in the PDF, forwarded for page_number input bounds */
    totalPages?: number;
    /** Forwarded to the toolbar's search input for the panel-level slash shortcut. */
    searchInputRef?: Ref<HTMLInputElement>;
    /**
     * Slice 7. Bubbles up to `DocumentDetailPanel`'s `pdfModal` state when
     * the user clicks "Open in document" from a row's source-quote popover.
     */
    onOpenInDocument?: (payload: {
        documentId: number;
        /** `null` for doc-level deep links (e.g., COI page pill). */
        propertyId: number | null;
        pageNumber: number;
        fieldName: string;
        source: string | null;
    }) => void;
}

function isEditableActiveElement(): boolean {
    const el = document.activeElement;
    if (!el || el === document.body) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if ((el as HTMLElement).isContentEditable) return true;
    return false;
}

function fieldMatchesQuery(
    field: IExtractedFieldResponse,
    needle: string
): boolean {
    if (field.field_name.toLowerCase().includes(needle)) return true;
    return field.items.some((item) =>
        item.properties.some(
            (prop) =>
                prop.property_name.toLowerCase().includes(needle) ||
                (prop.value?.toLowerCase().includes(needle) ?? false)
        )
    );
}

function customFieldMatchesQuery(
    cf: ICustomFieldResponse,
    needle: string
): boolean {
    if (cf.name.toLowerCase().includes(needle)) return true;
    return cf.properties.some(
        (prop) =>
            prop.name.toLowerCase().includes(needle) ||
            (prop.value?.toLowerCase().includes(needle) ?? false)
    );
}

function groupHasSearchMatches(
    group: IDocumentFieldsByPolicyType,
    needle: string
): boolean {
    if (!needle) return true;
    if (group.scalar_fields.some((f) => fieldMatchesQuery(f, needle)))
        return true;
    if (group.array_fields.some((f) => fieldMatchesQuery(f, needle)))
        return true;
    return group.custom_fields.some((cf) =>
        customFieldMatchesQuery(cf, needle)
    );
}

type SelectableItem =
    | {
          label: string;
          kind: "group";
          groups: IDocumentFieldsByPolicyType[];
      }
    | { label: typeof COI_LABEL; kind: "coi" };

// Per-kind icon prefix for the view selector. The wrapper carries a stable
// `data-icon` attribute so tests can lock in the icon prefix without
// depending on Lucide's internal class hashing; Lucide may rename or
// shorten its `lucide-*` classes between versions.
function SelectViewIcon({ kind }: { kind: SelectableItem["kind"] }) {
    return kind === "coi" ? (
        <BadgeCheck
            data-icon="badge-check"
            aria-hidden
            className="text-muted-foreground/80 size-3.5 shrink-0"
        />
    ) : (
        <Shield
            data-icon="shield"
            aria-hidden
            className="text-muted-foreground/80 size-3.5 shrink-0"
        />
    );
}

export function ExtractedFieldsSection(props: ExtractedFieldsSectionProps) {
    const {
        documentId,
        data: dataProp,
        coi = null,
        isLoading: isLoadingProp,
        isError: isErrorProp,
        onRetry,
        currentPage,
        totalPages,
        searchInputRef: externalSearchInputRef,
        onOpenInDocument,
    } = props;

    const { user } = useAuth();
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showEmpty, setShowEmpty] = useState(false);
    const internalSearchRef = useRef<HTMLInputElement>(null);

    // Prefer the parent's ref when the panel drives the slash shortcut; fall
    // back to a section-local ref so tests render without a panel still focus.
    const searchInputRef = externalSearchInputRef ?? internalSearchRef;

    // Fallback fields query: only fires when the consumer hasn't passed any
    // of the lifecycle props. Tests that pass `data`/`isLoading`/`isError`
    // explicitly drive the section without a network call. Test 14 omits all
    // three, so the query fires and MSW can intercept it for the error stub.
    const consumerDrivesLifecycle =
        dataProp !== undefined ||
        isLoadingProp !== undefined ||
        isErrorProp !== undefined;
    const fallbackEnabled = !consumerDrivesLifecycle && documentId != null;
    const fallbackQuery = useQuery({
        queryKey: ["frontend-document-extracted-fields", documentId],
        queryFn: () => getDocumentExtractedFields(documentId),
        enabled: fallbackEnabled,
        retry: false,
    });

    const data = dataProp ?? fallbackQuery.data;
    const isLoading =
        (isLoadingProp ?? false) ||
        (fallbackEnabled && fallbackQuery.isLoading);
    const isError = isErrorProp ?? (fallbackEnabled && fallbackQuery.isError);
    const handleRetry = onRetry ?? (() => fallbackQuery.refetch());

    // Slash-shortcut: window-scoped so the section's own tests focus the input
    // without a panel parent. The editable-element guard keeps "/" passing
    // through to inputs/textareas the user is actively typing into. Modifier
    // keys are excluded so Cmd+/ / Ctrl+/ don't hijack focus from app/IDE
    // shortcuts that share the slash character.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "/") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (isEditableActiveElement()) return;
            const node =
                searchInputRef && "current" in searchInputRef
                    ? searchInputRef.current
                    : null;
            if (!node) return;
            e.preventDefault();
            node.focus();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [searchInputRef]);

    // Reset toolbar state when the panel switches to a different document.
    // Without this, search ("liability") and the empty-toggle bleed across
    // docs because the section stays mounted while `documentId` flips.
    useEffect(() => {
        setSearchQuery("");
        setShowEmpty(false);
        setSelectedLabel(null);
    }, [documentId]);

    const hiddenFieldsCount = useMemo(
        () => countHiddenFields(data, coi),
        [data, coi]
    );

    const adminMenu =
        user?.is_admin && data ? (
            <AdminExtractionMenu
                documentId={documentId}
                fieldExtractionStatus={data.field_extraction_status}
                fieldExtractionAvailable={data.field_extraction_available}
                formExtractionAvailable={data.form_extraction_available}
            />
        ) : null;

    // Search row is suppressed in states where there's nothing to search:
    // loading, processing/extracting, and the various empty branches. Only
    // the happy-path render (extracted items present) shows it.
    //
    // `extractionComplete` gates the hidden-empty toggle so it doesn't appear
    // during pending/processing/failed/not_applicable, even when a partial
    // result (e.g. COI-only run) would otherwise satisfy `hiddenCount > 0`.
    const extractionComplete = data?.field_extraction_status === "complete";
    const renderToolbar = (showSearch = true) => (
        <FieldsToolbar
            searchInputRef={searchInputRef}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            hiddenCount={hiddenFieldsCount}
            showEmpty={showEmpty}
            onToggleEmpty={() => setShowEmpty((v) => !v)}
            adminMenu={adminMenu}
            showSearch={showSearch}
            extractionComplete={extractionComplete}
        />
    );

    // 1. Request error wins over every data-state branch. When the query
    //    fails, `data` is unreliable, so we surface the failure atomically.
    if (isError) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <FieldsErrorState onRetry={handleRetry} />
            </div>
        );
    }

    // 2. Loading silent: no banner copy, just the toolbar + skeleton rows.
    if (isLoading && !data) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <FieldsLoadingState variant="fetching" />
            </div>
        );
    }

    if (!data) {
        return <div className="space-y-3">{renderToolbar(false)}</div>;
    }

    const {
        field_extraction_status,
        field_extraction_available,
        form_extraction_available,
        field_groups = [],
    } = data;
    const anyPipelineAvailable =
        field_extraction_available || form_extraction_available;

    // 3. Extracting: server-reported processing status.
    if (field_extraction_status === "processing") {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <FieldsLoadingState variant="extracting" />
            </div>
        );
    }

    // Each policy type gets its own Select option (Commercial General
    // Liability, Commercial Property, etc.). COI joins as a peer entry when
    // present. Only the active item renders below the Select.
    const items: SelectableItem[] = [
        ...field_groups.map((g) => ({
            label: g.policy_type?.trim() || POLICY_FALLBACK_LABEL,
            kind: "group" as const,
            groups: [g],
        })),
        ...(coi ? [{ label: COI_LABEL, kind: "coi" } as SelectableItem] : []),
    ];

    // 4. Empty-Extract: pending/not_applicable + at least one pipeline.
    //    "No fields extracted yet" reads state→action; the CTA is "Extract
    //    fields" instead of the older action-first "Run extraction" label.
    if (
        items.length === 0 &&
        anyPipelineAvailable &&
        (field_extraction_status === "pending" ||
            field_extraction_status === "not_applicable")
    ) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <Empty className="border-none py-4">
                    <EmptyHeader>
                        <EmptyMedia variant="icon">
                            <Sparkles className="text-copper" />
                        </EmptyMedia>
                        <EmptyTitle>No fields extracted yet</EmptyTitle>
                        <EmptyDescription>
                            Run extraction to pull policy info, premiums,
                            limits, deductibles, and COI details from this
                            document.
                        </EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                        <RunExtractionButton
                            documentId={documentId}
                            fieldExtractionStatus={field_extraction_status}
                            fieldExtractionAvailable={
                                field_extraction_available
                            }
                            formExtractionAvailable={form_extraction_available}
                            pendingLabel="Extract fields"
                        />
                    </EmptyContent>
                </Empty>
            </div>
        );
    }

    // 5. Empty-None terminal: no pipelines available. Neutral icon, no CTA.
    //    `FileX2`'s destructive connotation belongs to the failed branch.
    if (!anyPipelineAvailable) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <Empty className="border-none py-4">
                    <EmptyHeader>
                        <EmptyMedia variant="icon">
                            <FileSearch2 />
                        </EmptyMedia>
                        <EmptyTitle>No fields to extract</EmptyTitle>
                        <EmptyDescription>
                            We scanned this document but couldn't find any
                            policy data to pull. This document may not contain
                            extractable insurance fields.
                        </EmptyDescription>
                    </EmptyHeader>
                </Empty>
            </div>
        );
    }

    // Failed extraction with no items: surface the failure + re-run affordance.
    if (field_extraction_status === "failed" && items.length === 0) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <Empty className="border-none py-4">
                    <EmptyHeader>
                        <EmptyMedia variant="icon">
                            <AlertCircle className="text-destructive" />
                        </EmptyMedia>
                        <EmptyTitle>Extraction failed</EmptyTitle>
                        <EmptyDescription>
                            Try running extraction again.
                        </EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                        <RunExtractionButton
                            documentId={documentId}
                            fieldExtractionStatus={field_extraction_status}
                            fieldExtractionAvailable={
                                field_extraction_available
                            }
                            formExtractionAvailable={form_extraction_available}
                        />
                    </EmptyContent>
                </Empty>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="space-y-3">
                {renderToolbar(false)}
                <Empty className="border-none py-4">
                    <EmptyHeader>
                        <EmptyMedia variant="icon">
                            <Inbox />
                        </EmptyMedia>
                        <EmptyTitle>No extracted fields</EmptyTitle>
                        <EmptyDescription>
                            No structured fields were found in this document.
                        </EmptyDescription>
                    </EmptyHeader>
                </Empty>
            </div>
        );
    }

    const activeLabel = items.some((i) => i.label === selectedLabel)
        ? selectedLabel!
        : items[0].label;

    const activeItem = items.find((i) => i.label === activeLabel)!;

    // Hybrid: data is present (e.g. COI extracted) but field extraction is
    // still pending or failed: keep the run/re-extract affordance visible.
    const showStandaloneRunButton =
        field_extraction_status === "pending" ||
        field_extraction_status === "failed";

    const trimmedQuery = searchQuery.trim();
    const needle = trimmedQuery.toLowerCase();
    const activeMatchesSearch =
        !needle ||
        (activeItem.kind === "group"
            ? activeItem.groups.some((g) => groupHasSearchMatches(g, needle))
            : true);

    return (
        <div className="space-y-3">
            {renderToolbar()}

            {showStandaloneRunButton && (
                <RunExtractionButton
                    documentId={documentId}
                    fieldExtractionStatus={field_extraction_status}
                    fieldExtractionAvailable={field_extraction_available}
                    formExtractionAvailable={form_extraction_available}
                />
            )}

            {items.length > 1 ? (
                <Select value={activeLabel} onValueChange={setSelectedLabel}>
                    <SelectTrigger
                        aria-label="Extraction view"
                        className="w-full"
                    >
                        <SelectViewIcon kind={activeItem.kind} />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {items.map((item) => (
                            <SelectItem key={item.label} value={item.label}>
                                <SelectViewIcon kind={item.kind} />
                                {item.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : (
                <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                    <SelectViewIcon kind={items[0].kind} />
                    {items[0].label}
                </h4>
            )}

            {!activeMatchesSearch ? (
                <p className="text-muted-foreground py-4 text-center text-sm">
                    No fields match `{trimmedQuery}`
                </p>
            ) : activeItem.kind === "group" ? (
                <ActiveGroupBody
                    documentId={documentId}
                    group={activeItem.groups[0]}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    showEmpty={showEmpty}
                    searchQuery={needle}
                    onOpenInDocument={onOpenInDocument}
                />
            ) : (
                <ExtractedCOISection
                    documentId={documentId}
                    coi={coi!}
                    showEmpty={showEmpty}
                    onOpenInDocument={onOpenInDocument}
                />
            )}
        </div>
    );
}

function FieldsErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div
            role="alert"
            className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center"
        >
            <TriangleAlert
                className="text-muted-foreground size-7"
                strokeWidth={1.8}
            />
            <div className="space-y-1">
                <div className="text-sm font-semibold">
                    Couldn't load fields
                </div>
                <div className="text-muted-foreground text-xs">
                    Check your connection and try again.
                </div>
            </div>
            <Button variant="outline" size="sm" onClick={onRetry}>
                Try again
            </Button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// FieldGroupContent
// ---------------------------------------------------------------------------

// Unified row used in the merged scalar list; preserves a stable key and the
// `isCustom` flag so user-added properties show the PencilLine identifier.
type MergedPropertyRow = {
    key: string;
    propertyId: number;
    propertyName: string;
    value: string | null;
    pageNumber: number | null;
    isEdited: boolean;
    isCustom: boolean;
    /** Verbatim source quote (Slice 1 wire shape). Custom-field rows have none. */
    source: string | null;
};

function toExtractedRow(prop: IExtractedPropertyValue): MergedPropertyRow {
    return {
        key: `extracted-${prop.id}`,
        propertyId: prop.id,
        propertyName: prop.property_name,
        value: prop.value,
        pageNumber: prop.page_number,
        isEdited: prop.is_edited,
        isCustom: false,
        source: prop.source,
    };
}

function toCustomRow(prop: ICustomFieldPropertyResponse): MergedPropertyRow {
    return {
        key: `custom-${prop.id}`,
        propertyId: prop.id,
        propertyName: prop.name,
        value: prop.value,
        pageNumber: prop.page_number,
        isEdited: false,
        isCustom: true,
        source: null,
    };
}

// Per-group visible/hidden row counts. Counts every property across scalar,
// array, and custom fields. The Slice 9 group header surfaces these as the
// `<visible> · <hidden> empty` count badge format.
function countGroupRows(group: IDocumentFieldsByPolicyType): {
    visible: number;
    hidden: number;
} {
    let visible = 0;
    let hidden = 0;
    const tally = (value: string | null | undefined) => {
        if (isEmptyPropertyValue(value)) hidden++;
        else visible++;
    };
    for (const field of group.scalar_fields ?? []) {
        for (const item of field.items ?? []) {
            for (const prop of item.properties ?? []) tally(prop.value);
        }
    }
    for (const field of group.array_fields ?? []) {
        for (const item of field.items ?? []) {
            for (const prop of item.properties ?? []) tally(prop.value);
        }
    }
    for (const cf of group.custom_fields ?? []) {
        for (const prop of cf.properties ?? []) tally(prop.value);
    }
    return { visible, hidden };
}

interface ActiveGroupBodyProps {
    documentId: number;
    group: IDocumentFieldsByPolicyType;
    currentPage?: number;
    totalPages?: number;
    showEmpty: boolean;
    searchQuery: string;
    onOpenInDocument?: (payload: {
        documentId: number;
        /** `null` for doc-level deep links (e.g., COI page pill). */
        propertyId: number | null;
        pageNumber: number;
        fieldName: string;
        source: string | null;
    }) => void;
}

// Renders the body of the actively selected policy-type. The Select trigger
// owns the policy_type label, so this component drops straight into the
// rows. The `allEmpty` short-circuit keeps the "Add Custom Field" affordance
// reachable when every property in the group is blank.
function ActiveGroupBody({
    documentId,
    group,
    currentPage,
    totalPages,
    showEmpty,
    searchQuery,
    onOpenInDocument,
}: ActiveGroupBodyProps) {
    const { visible, hidden } = countGroupRows(group);
    const allEmpty = visible === 0 && hidden > 0;

    if (allEmpty) {
        return (
            <div className="space-y-3">
                <p className="text-muted-foreground px-3 py-3 text-xs">
                    All empty
                </p>
                <AddCustomFieldButton
                    documentId={documentId}
                    policyTypeId={group.policy_type_id}
                    currentPage={currentPage}
                    totalPages={totalPages}
                />
            </div>
        );
    }

    return (
        <FieldGroupContent
            documentId={documentId}
            group={group}
            currentPage={currentPage}
            totalPages={totalPages}
            showEmpty={showEmpty}
            searchQuery={searchQuery}
            onOpenInDocument={onOpenInDocument}
        />
    );
}

function FieldGroupContent({
    documentId,
    group,
    currentPage,
    totalPages,
    showEmpty,
    searchQuery,
    onOpenInDocument,
}: {
    documentId: number;
    group: IDocumentFieldsByPolicyType;
    currentPage?: number;
    totalPages?: number;
    showEmpty: boolean;
    /** Lowercased + trimmed query from the toolbar; "" disables filtering. */
    searchQuery: string;
    onOpenInDocument?: (payload: {
        documentId: number;
        /** `null` for doc-level deep links (e.g., COI page pill). */
        propertyId: number | null;
        pageNumber: number;
        fieldName: string;
        source: string | null;
    }) => void;
}) {
    const isEmpty =
        group.scalar_fields.length === 0 &&
        group.array_fields.length === 0 &&
        group.custom_fields.length === 0;

    // Partition custom fields: bridges merge into matching scalar fields by
    // field_definition_id; standalones render inline as scalar-like sections.
    // A bridge whose field_definition_id has no matching scalar field falls
    // through to standalone so the data is never silently hidden.
    const scalarFieldDefIds = new Set(
        group.scalar_fields.map((f) => f.field_definition_id)
    );
    const bridgesByFieldDef = new Map<number, ICustomFieldResponse[]>();
    const standaloneCustomFields: ICustomFieldResponse[] = [];
    for (const cf of group.custom_fields) {
        if (
            cf.field_definition_id != null &&
            scalarFieldDefIds.has(cf.field_definition_id)
        ) {
            const list = bridgesByFieldDef.get(cf.field_definition_id) ?? [];
            list.push(cf);
            bridgesByFieldDef.set(cf.field_definition_id, list);
        } else {
            standaloneCustomFields.push(cf);
        }
    }

    // Build merged rows per scalar field. Both extracted and bridge custom
    // properties honor `showEmpty` so the "N hidden" counter stays consistent.
    const scalarRenderItems = group.scalar_fields.map((field) => {
        const extractedProps = field.items.flatMap((item) => item.properties);
        const visibleExtracted = showEmpty
            ? extractedProps
            : extractedProps.filter((p) => !isEmptyPropertyValue(p.value));

        const bridges = bridgesByFieldDef.get(field.field_definition_id) ?? [];
        const customProps = bridges.flatMap((cf) => cf.properties);
        const visibleCustom = showEmpty
            ? customProps
            : customProps.filter((p) => !isEmptyPropertyValue(p.value));

        const rows: MergedPropertyRow[] = [
            ...visibleExtracted.map(toExtractedRow),
            ...visibleCustom.map(toCustomRow),
        ];

        // First bridge (if any) seeds new properties; otherwise create one
        // via bridge mode in AddPropertyInlineForm.
        const bridge = bridges[0];

        return { field, rows, bridge };
    });

    const visibleScalarItems = scalarRenderItems
        .filter(({ rows }) => showEmpty || rows.length > 0)
        .filter(
            ({ field }) => !searchQuery || fieldMatchesQuery(field, searchQuery)
        );

    // Drop items where ALL properties are empty when `showEmpty=false`,
    // but keep `item.properties` UNFILTERED so the per-card stable key
    // (smallest property id) does not flip when the user toggles
    // `showEmpty`. The card filters its own row list at render time.
    const visibleArrayFields = group.array_fields
        .map((field) => {
            if (showEmpty) return field;
            const items = field.items.filter((item) =>
                item.properties.some((p) => !isEmptyPropertyValue(p.value))
            );
            return { ...field, items };
        })
        .filter((field) => showEmpty || field.items.length > 0)
        .filter(
            (field) => !searchQuery || fieldMatchesQuery(field, searchQuery)
        );

    const visibleStandaloneCustomFields = standaloneCustomFields.filter(
        (cf) => !searchQuery || customFieldMatchesQuery(cf, searchQuery)
    );

    const customFieldsHaveAnyValue = visibleStandaloneCustomFields.some((cf) =>
        cf.properties.some((p) => !isEmptyPropertyValue(p.value))
    );

    const allHiddenByFilter =
        !isEmpty &&
        !showEmpty &&
        visibleScalarItems.length === 0 &&
        visibleArrayFields.length === 0 &&
        !customFieldsHaveAnyValue;

    return (
        <div className="space-y-4">
            {isEmpty && (
                <p className="text-muted-foreground text-sm">
                    No fields in this policy type.
                </p>
            )}

            {allHiddenByFilter && (
                <p className="text-muted-foreground text-sm">
                    All fields are empty. Use the toggle above to reveal them.
                </p>
            )}

            {visibleScalarItems.map(({ field, rows, bridge }) => (
                <Collapsible
                    key={field.field_definition_id}
                    defaultOpen
                    data-testid="field-definition-group"
                >
                    <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-1 text-left">
                        <ChevronRight
                            aria-hidden
                            className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90"
                        />
                        <span className="text-foreground text-sm font-semibold">
                            {field.field_name}
                        </span>
                        {rows.length > 0 && (
                            <span className="text-muted-foreground text-xs font-normal">
                                {rows.length}
                            </span>
                        )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="border-border divide-border divide-y rounded-lg border">
                            {rows.map((row) => (
                                <ExtractedFieldRow
                                    key={row.key}
                                    documentId={documentId}
                                    propertyId={row.propertyId}
                                    propertyName={row.propertyName}
                                    value={row.value ?? ""}
                                    isEdited={row.isEdited}
                                    pageNumber={row.pageNumber}
                                    totalPages={totalPages}
                                    isCustom={row.isCustom}
                                    source={row.source}
                                    fieldName={field.field_name}
                                    onOpenInDocument={onOpenInDocument}
                                />
                            ))}
                            <AddPropertyInlineForm
                                documentId={documentId}
                                customFieldId={bridge?.id}
                                policyTypeId={
                                    bridge ? undefined : group.policy_type_id
                                }
                                fieldDefinitionId={
                                    bridge
                                        ? undefined
                                        : field.field_definition_id
                                }
                                fieldName={
                                    bridge ? undefined : field.field_name
                                }
                                currentPage={currentPage}
                                totalPages={totalPages}
                            />
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            ))}

            {/* Standalone custom fields mirror the scalar collapsible layout. */}
            {visibleStandaloneCustomFields.map((cf) => {
                const visibleProps = showEmpty
                    ? cf.properties
                    : cf.properties.filter(
                          (p) => !isEmptyPropertyValue(p.value)
                      );
                return (
                    <Collapsible
                        key={`custom-${cf.id}`}
                        defaultOpen
                        data-testid="field-definition-group"
                    >
                        <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-1 text-left">
                            <ChevronRight
                                aria-hidden
                                className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90"
                            />
                            <span className="text-foreground text-sm font-semibold">
                                {cf.name}
                            </span>
                            {visibleProps.length > 0 && (
                                <span className="text-muted-foreground text-xs font-normal">
                                    {visibleProps.length}
                                </span>
                            )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="border-border divide-border divide-y rounded-lg border">
                                {visibleProps.map((prop) => (
                                    <ExtractedFieldRow
                                        key={`custom-${prop.id}`}
                                        documentId={documentId}
                                        propertyId={prop.id}
                                        propertyName={prop.name}
                                        value={prop.value ?? ""}
                                        isEdited={false}
                                        pageNumber={prop.page_number}
                                        totalPages={totalPages}
                                        isCustom
                                        source={null}
                                        fieldName={cf.name}
                                        onOpenInDocument={onOpenInDocument}
                                    />
                                ))}
                                <AddPropertyInlineForm
                                    documentId={documentId}
                                    customFieldId={cf.id}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                />
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                );
            })}

            {/*
              Array items are READ-ONLY in this redesign. There is no
              "Add location" / "Add array item" affordance; only the
              existing per-field add-property flow (in the group footer
              via AddCustomFieldButton above) is supported.

              If you find yourself adding an "Add location" button here,
              that is out of scope for the DDPR redesign. Origin doc R31
              explicitly excludes it; if a stakeholder asks, plan it as a
              separate feature.
            */}
            {visibleArrayFields.map((field) => {
                // `array_item_label` is the schema's single source of truth
                // for the item-badge fallback ("Location 1" / "Location 2").
                // Avoid singularizing `field_name` heuristically; that breaks
                // for non-pluralized or non-English labels.
                const itemLabel = field.array_item_label?.trim() || "Item";
                return (
                    <Collapsible
                        key={field.field_definition_id}
                        defaultOpen
                        data-testid="array-group"
                    >
                        <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-1 text-left">
                            <ChevronRight
                                aria-hidden
                                className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90"
                            />
                            <span className="text-foreground text-sm font-semibold">
                                {field.field_name}
                            </span>
                            <span className="text-muted-foreground text-xs font-normal">
                                {field.items.length}
                            </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="space-y-1">
                                {field.items.map((item, index) => (
                                    <ArrayItemCard
                                        // Stable identifier that survives item
                                        // reordering between polls. See
                                        // `getStableItemKey` for fallback rules.
                                        key={getStableItemKey(item, field)}
                                        item={item}
                                        index={index}
                                        itemLabel={itemLabel}
                                        documentId={documentId}
                                        totalPages={totalPages}
                                        onOpenInDocument={onOpenInDocument}
                                        fieldName={field.field_name}
                                        showEmpty={showEmpty}
                                    />
                                ))}
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                );
            })}

            <AddCustomFieldButton
                documentId={documentId}
                policyTypeId={group.policy_type_id}
                currentPage={currentPage}
                totalPages={totalPages}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Array fields: collapsible item cards
// ---------------------------------------------------------------------------

type OpenInDocumentHandler = NonNullable<
    ExtractedFieldsSectionProps["onOpenInDocument"]
>;

/**
 * Derive the one-line summary for an array-item card header.
 *
 * Order of preference:
 *   1. `item.display_name` (when set on `IDocumentExtractedFieldItem`).
 *   2. Property named `"Address"` (Property Coverage locations).
 *   3. Property named `"Location Address"` (alternate naming convention).
 *   4. Omit the summary line. Do NOT fall back to "first non-empty value".
 *
 * **Why deterministic, not first-non-empty:** the property iteration order
 * on `IDocumentExtractedFieldItem` is data-driven; it can put a numeric
 * value (e.g., `square_footage: 1200`, `deductible: 500`) before the
 * address field. Picking that as the summary makes array items look broken
 * because the scannable identity of the item is the address, not an
 * arbitrary number.
 *
 * Returning `null` is fine; the collapsible item is still navigable via
 * the label badge + visible-field count, and an honest empty is better
 * than a misleading summary.
 *
 * Related: Slice 12 (`insurescope-kwyek.12`), Plan Unit 11, origin doc
 * R29–R31. Reuses Slice 6 (`ExtractedFieldRow`) for expanded bodies and
 * Slice 9's canonical `Collapsible` + chevron pattern for the card frame.
 */
function deriveItemSummary(item: IDocumentExtractedFieldItem): string | null {
    if (item.display_name?.trim()) return item.display_name;
    const candidates = ["Address", "Location Address"];
    for (const name of candidates) {
        const prop = item.properties.find((p) => p.property_name === name);
        if (prop?.value) return prop.value;
    }
    return null;
}

// Stable React key for an array item. `IDocumentExtractedFieldItem` does not
// expose its own primary key, so we derive a stable identifier from the
// smallest property id; property ids persist across reorders, even when the
// API shuffles `array_index`. Rejecting `array_index` is load-bearing: if the
// poll cycle reorders items, an index-keyed Collapsible silently flips which
// item appears expanded. Falls back to a field-scoped index key when an item
// has no properties yet (rare but defensive, e.g., an item that was just
// created server-side and hasn't been populated).
function getStableItemKey(
    item: IDocumentExtractedFieldItem,
    field: IExtractedFieldResponse
): string {
    if (item.properties.length > 0) {
        const minId = Math.min(...item.properties.map((p) => p.id));
        return `prop-${minId}`;
    }
    return `idx-${field.field_definition_id}-${item.array_index}`;
}

interface ArrayItemCardProps {
    item: IDocumentExtractedFieldItem;
    /** Render position. Drives `defaultOpen` so the first item ships expanded. */
    index: number;
    /** Schema-defined singular label (e.g., "Location"). Always non-empty. */
    itemLabel: string;
    documentId: number;
    totalPages?: number;
    onOpenInDocument?: OpenInDocumentHandler;
    fieldName: string;
    /**
     * When false, properties whose value is empty/null are filtered out of
     * the row list and the visible-count badge. Filtering at the card level
     * (instead of upstream) keeps `getStableItemKey` deriving from the full
     * unfiltered property set so the React key (and therefore the
     * Collapsible's open/closed state) survives a `showEmpty` toggle.
     */
    showEmpty: boolean;
}

function ArrayItemCard({
    item,
    index,
    itemLabel,
    documentId,
    totalPages,
    onOpenInDocument,
    fieldName,
    showEmpty,
}: ArrayItemCardProps) {
    // `display_name?.trim() ||` covers null/undefined/"" uniformly so an
    // empty-string display_name doesn't render as a blank badge.
    const badge =
        item.display_name?.trim() || `${itemLabel} ${item.array_index + 1}`;
    const summary = deriveItemSummary(item);
    const visibleProperties = showEmpty
        ? item.properties
        : item.properties.filter((p) => !isEmptyPropertyValue(p.value));
    const visibleCount = visibleProperties.length;

    return (
        <Collapsible data-testid="array-item-card" defaultOpen={index === 0}>
            <div className="border-border overflow-hidden rounded-lg border">
                <CollapsibleTrigger className="group bg-muted/40 hover:bg-muted/60 data-[state=open]:border-border flex w-full items-center gap-2 px-3 py-2 text-left transition-colors data-[state=open]:border-b">
                    <ChevronRight
                        aria-hidden
                        className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90"
                    />
                    <span
                        data-testid="array-item-label-badge"
                        className="text-foreground bg-muted shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
                    >
                        {badge}
                    </span>
                    {summary !== null && (
                        <span
                            data-testid="array-item-summary"
                            className="text-muted-foreground min-w-0 flex-1 truncate text-xs"
                        >
                            {summary}
                        </span>
                    )}
                    <span className="text-muted-foreground ml-auto shrink-0 text-xs font-medium">
                        {visibleCount}
                    </span>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="divide-border divide-y">
                        {visibleProperties.map((prop) => (
                            <ExtractedFieldRow
                                key={prop.id}
                                documentId={documentId}
                                propertyId={prop.id}
                                propertyName={prop.property_name}
                                value={prop.value ?? ""}
                                isEdited={prop.is_edited}
                                pageNumber={prop.page_number}
                                totalPages={totalPages}
                                source={prop.source}
                                fieldName={fieldName}
                                onOpenInDocument={onOpenInDocument}
                            />
                        ))}
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}
